import { ActionIcon, Button, Chip, Menu, Tabs, TextInput, UnstyledButton } from '@mantine/core';
import {
  IconCalendarClock,
  IconClipboardPlus,
  IconClipboardText,
  IconDots,
  IconEye,
  IconFileImport,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDay } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { useShell } from '../../shell-context.js';
import { vesselTime } from '../personnel/ship-clock.js';
import { errorCode } from '../personnel/source.js';
import { SortableTh, TablePager } from '../purchasing/table.js';
import { CmImportDialog } from './CmImportDialog.js';
import type { PointList } from './condition-types.js';
import { utcToShipLocal } from './counter-form.js';
import { IntervalChangeDialog } from './IntervalChangeDialog.js';
import { JobCardDrawer } from './JobCardDrawer.js';
import { type JobFormValues, toJobInput } from './job-form.js';
import { decisionKind, OfficeDecisionDrawer } from './OfficeDecisionDrawer.js';
import classes from './Pms.module.css';
import { MaintenancePlanPanel, PmsCalendar, UpcomingTasksPanel } from './PmsPanels.js';
import work from './PmsWork.module.css';
import {
  LoadFailed,
  PmsBadge,
  RowsSkeleton,
  usePmsQuery,
  useVesselChoice,
  VesselPicker,
} from './PmsWorkParts.js';
import { PostponeDialog } from './PostponeDialog.js';
import { PostponementDecisionDrawer } from './PostponementDecisionDrawer.js';
import {
  calendarMarks,
  filterPlanFlags,
  frequencyLabel,
  jobActions,
  openWorkOrderFor,
  PLAN_FLAG_KEYS,
  type PlanFlags,
  paginate,
  planFlagsSpanPeriods,
  type Sort,
} from './plan-view.js';
import { SetupChangesBanner } from './SetupChangesBanner.js';
import type { PmsSource } from './source.js';
import type {
  EquipmentTree,
  HistoryEntry,
  InstructionOption,
  IntervalHistoryEntry,
  JobChangeEntry,
  JobList,
  JobView,
  PendingItem,
  PlanItem,
  PlanView,
  WorkOrderView,
} from './types.js';
import { isQueuedChange, setupChangesPath } from './types.js';

export interface MaintenancePlanScreenProps {
  source: PmsSource;
  now?: () => Date;
  /**
   * "Instructions" sekmesinin içeriği (K38): uygulama M8a3'ün kütüphane ekranını
   * `pms_instructions` bölümüyle verir; verilmezse sekme çizilmez.
   */
  instructions?: ReactNode;
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | {
      kind: 'ready';
      vesselId: string;
      tree: EquipmentTree;
      plan: PlanView;
      jobs: JobList;
      workOrders: WorkOrderView[];
      history: HistoryEntry[];
      pending: PendingItem[];
      instructions: InstructionOption[];
      /** Ölçüm noktaları ve yazma yetkisi (M7c). */
      points: PointList;
    };

const systemNow = () => new Date();

const NO_POINTS: PointList = { items: [], can: { create: false, edit: false, delete: false } };

/** Ekipman ya da atası klasla ilgili ya da iş klas sörveyi: ertelemede klas referansı (K10). */
export function classItemOf(tree: EquipmentTree | null, job: JobView | undefined): boolean {
  if (!tree || !job) return false;
  if (job.jobType === 'class_survey' || job.ceSurvey === true) return true;
  const byId = new Map(tree.nodes.map((n) => [n.id, n]));
  let cursor = byId.get(job.equipmentId);
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor.id)) {
    if (cursor.classRelevant) return true;
    seen.add(cursor.id);
    cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
  }
  return false;
}

/**
 * "Import CM data" (K20; plan "Eylem × uygulama"): gemide eşik ve değer, ofiste yalnız kurulum
 * devrindeki gemide (nokta listesinin `can.import`'u); etkinleşmiş gemide gizli.
 */
export function cmImportAllowed(
  source: Pick<PmsSource, 'scope' | 'cm'>,
  points: Pick<PointList, 'can'> | null,
): boolean {
  return Boolean(source.cm) && points?.can.import === true;
}

/**
 * Bu ekranda verilebilen karar ("Eylem × uygulama"; K10, K11): gemide yalnız kaptanın erteleme
 * adımı; ofiste ertelemenin ve aralık uzatmasının ofis adımı. İş kaydı onayı iş emri detayındadır.
 */
export function decisionAllowed(
  source: Pick<PmsSource, 'scope' | 'work' | 'office'>,
  item: Pick<PendingItem, 'office' | 'recordType'>,
): boolean {
  const kind = decisionKind(item);
  return source.scope === 'ship'
    ? !item.office && kind === 'postponement' && Boolean(source.work)
    : item.office && Boolean(source.office) && (kind === 'postponement' || kind === 'interval');
}

/**
 * Maintenance (PMS) ▸ Maintenance Plan (`/pms/plan`; plan M7 Task 14a): referansın "Maintenance
 * Plan", "Upcoming Tasks" ve "PMS Calendar" panelleri büyütülmüş; sayfa içi sekmeler Plan, Job
 * Library ve Instructions (K38). Job Card çekmecesi, erteleme ve aralık değişikliği pencereleri,
 * bekleyen kararlar. Karar çekmecesi rozetten ve zilden (`?decide=<kayıt>`) açılır: gemide kaptan
 * erteleme adımını, web'de süperintendent erteleme ve aralık uzatması adımını verir (K10, K11).
 */
export function MaintenancePlanScreen({
  source,
  now = systemNow,
  instructions,
}: MaintenancePlanScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const [query, setQuery] = usePmsQuery();
  const choice = useVesselChoice(source, query.get('vessel'));
  const vesselId = choice.vesselId;
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [reloads, setReloads] = useState(0);
  const [intervalJob, setIntervalJob] = useState<JobView | null>(null);
  // Etkin gemide ofisin aralık kararı ve açık işin aralık geçmişi (ADR 0104)
  const [officeIntervalJob, setOfficeIntervalJob] = useState<JobView | null>(null);
  const [intervalHistory, setIntervalHistory] = useState<{
    jobId: string;
    entries: IntervalHistoryEntry[];
    changes: JobChangeEntry[];
  } | null>(null);
  const [postponing, setPostponing] = useState<PlanItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const tab = query.get('tab') ?? 'plan';
  const openJobId = query.get('job');
  const newJob = query.get('newJob') === '1';
  const decideId = query.get('decide');
  const postponeParam = query.get('postpone');

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!vesselId) return;
    let live = true;
    setData((current) =>
      current.kind === 'ready' && current.vesselId === vesselId ? current : { kind: 'loading' },
    );
    Promise.all([
      source.tree(vesselId),
      source.plan ? source.plan(vesselId) : Promise.resolve(null),
      source.jobs ? source.jobs(vesselId) : Promise.resolve(null),
      source.workOrders ? source.workOrders(vesselId) : Promise.resolve(null),
      source.history ? source.history(vesselId) : Promise.resolve([]),
      source.pending ? source.pending() : Promise.resolve([]),
      source.instructions ? source.instructions(vesselId).catch(() => []) : Promise.resolve([]),
      source.measurementPoints
        ? source.measurementPoints(vesselId).catch(() => NO_POINTS)
        : Promise.resolve(NO_POINTS),
    ])
      .then(([tree, plan, jobs, workOrders, history, pending, options, points]) => {
        if (!live) return;
        setData({
          kind: 'ready',
          vesselId,
          tree,
          plan: plan ?? {
            today: '',
            plans: [],
            summary: {
              total: 0,
              today: 0,
              overdue: 0,
              dueNext7Days: 0,
              dueNext30Days: 0,
              criticalOverdue: 0,
            },
          },
          jobs: jobs ?? { items: [], can: { create: false, edit: false, delete: false } },
          workOrders: workOrders?.items ?? [],
          history,
          pending: pending.filter((p) => p.vesselId === vesselId),
          instructions: options,
          points,
        });
      })
      .catch((error: unknown) => {
        if (live) setData({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reloads` yazmadan sonra yeniden okutur
  useEffect(() => {
    if (!openJobId || !source.intervalHistory) return;
    let live = true;
    // Değişiklik kaydı (DNV-CP-0206 Sec.2 [4.6] b; ADR 0133 §2) okunamazsa bölüm çizilmez
    const changes = source.jobChangeLog
      ? source.jobChangeLog(openJobId).catch(() => [])
      : Promise.resolve([]);
    Promise.all([source.intervalHistory(openJobId), changes])
      .then(([entries, log]) => {
        if (live) setIntervalHistory({ jobId: openJobId, entries, changes: log });
      })
      // Geçmiş okunamazsa bölüm çizilmez; Job Card açılır
      .catch(() => {
        if (live) setIntervalHistory({ jobId: openJobId, entries: [], changes: [] });
      });
    return () => {
      live = false;
    };
  }, [source, openJobId, reloads]);
  const ready = data.kind === 'ready' ? data : null;
  const timeZone = vesselTime(choice.vessel);
  const today = ready?.plan.today || utcToShipLocal(now(), timeZone).slice(0, 10);
  const plansByJob = useMemo(
    () => new Map((ready?.plan.plans ?? []).map((p) => [p.jobId, p])),
    [ready],
  );
  const jobsById = useMemo(() => new Map((ready?.jobs.items ?? []).map((j) => [j.id, j])), [ready]);
  // Klas, kritik, gecikmiş ve ertelenmiş süzgeçleri adreste [NR496 4.4.2b, 4.5.3e]
  const flagQuery = PLAN_FLAG_KEYS.map((key) => (query.get(key) === '1' ? key : '')).join(',');
  const flags = useMemo<PlanFlags>(
    () =>
      Object.fromEntries(
        flagQuery
          .split(',')
          .filter(Boolean)
          .map((key) => [key, true]),
      ),
    [flagQuery],
  );
  const isClass = useCallback(
    (item: { jobId: string }) => classItemOf(ready?.tree ?? null, jobsById.get(item.jobId)),
    [ready, jobsById],
  );
  const isAlarmTest = useCallback(
    (item: { jobId: string }) => jobsById.get(item.jobId)?.alarmTest === true,
    [jobsById],
  );
  const shownPlans = useMemo(
    () => (ready ? filterPlanFlags(ready.plan.plans, flags, isClass, isAlarmTest) : null),
    [ready, flags, isClass, isAlarmTest],
  );
  const openJob = openJobId ? (jobsById.get(openJobId) ?? null) : null;
  // Equipment List "…" ▸ Postpone `?postpone=<iş>` ile gelir
  const postponePlan =
    postponing ?? (postponeParam ? (plansByJob.get(postponeParam) ?? null) : null);
  const openPlan = openJob ? (plansByJob.get(openJob.id) ?? null) : null;
  const actions = jobActions(
    source.scope,
    ready?.jobs ?? null,
    openPlan?.lastDone.source === 'record',
  );
  const pendingItem = decideId
    ? (ready?.pending.find((p) => p.recordId === decideId) ?? null)
    : null;
  const canDecide = (item: PendingItem) => decisionAllowed(source, item);
  const decision = pendingItem && canDecide(pendingItem) ? pendingItem : null;
  const decidable = (ready?.pending ?? []).filter(canDecide);

  // Takvim, "Upcoming Tasks" ve plan tablosu aynı süzülmüş işlerden (ortak 2026-10-25)
  const flagged = PLAN_FLAG_KEYS.some((key) => flags[key] === true);
  const marks = useCallback(
    (month: string) =>
      calendarMarks({
        month,
        plans: shownPlans ?? [],
        workOrders: ready?.workOrders ?? [],
        history: ready?.history ?? [],
        ...(flagged ? { jobIds: new Set((shownPlans ?? []).map((p) => p.jobId)) } : {}),
      }),
    [ready, shownPlans, flagged],
  );

  const pendingFor = (plan: PlanItem, kind: 'postponement' | 'interval') =>
    decidable.find(
      (item) =>
        item.jobId === plan.jobId &&
        decisionKind(item) === (kind === 'postponement' ? 'postponement' : 'interval'),
    );

  const saveJob = async (values: JobFormValues, job: JobView | null) => {
    const editor = source.jobEditor;
    if (!editor || !vesselId) return;
    const input = toJobInput(values);
    if (job) {
      const { changeReason } = values;
      await editor.update(job.id, job.rowVersion, {
        ...input,
        ...(changeReason.trim() !== '' ? { changeReason: changeReason.trim() } : {}),
      });
      setQuery({ job: job.id, newJob: null });
    } else {
      const created = await editor.create(vesselId, values.equipmentId, input);
      // Kurulumdaki etkin gemi: iş gemide yazılır, buluta sonra gelir (ADR 0142 §3)
      setQuery({ job: isQueuedChange(created) ? null : created.id, newJob: null });
    }
    reload();
  };

  const createWorkOrder = async (plan: PlanItem) => {
    if (!source.work) return;
    try {
      const wo = await source.work.openWorkOrder({ jobId: plan.jobId });
      shell?.navigate(`/pms/work-orders?wo=${wo.id}`);
    } catch (error) {
      setNotice(t(`pms.errors.${errorCode(error)}`, { defaultValue: t('pms.errors.GENERIC') }));
    }
  };
  // İşin açık iş emri varsa menü yeni iş emri açmaz, ona götürür (ortak 2026-10-25)
  const openOrderOf = (plan: PlanItem) => openWorkOrderFor(ready?.workOrders ?? [], plan.jobId);

  const rowActions =
    source.scope === 'ship'
      ? (plan: PlanItem) => (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                aria-label={t('pms.work.plan.more', { code: plan.code })}
              >
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEye size={15} />}
                onClick={() => setQuery({ job: plan.jobId })}
              >
                {t('pms.work.plan.view')}
              </Menu.Item>
              {source.work ? (
                <>
                  <WorkOrderMenuItem
                    open={openOrderOf(plan)}
                    onOpen={(wo) => shell?.navigate(`/pms/work-orders?wo=${wo.id}`)}
                    onCreate={() => void createWorkOrder(plan)}
                  />
                  <Menu.Item
                    leftSection={<IconCalendarClock size={15} />}
                    disabled={plan.postponementPending}
                    onClick={() => setPostponing(plan)}
                  >
                    {t('pms.work.plan.postpone')}
                  </Menu.Item>
                </>
              ) : null}
            </Menu.Dropdown>
          </Menu>
        )
      : undefined;

  const title = t('menu.pmsPlan');
  if (choice.vessels !== null && choice.vessels.length === 0) {
    return (
      <div className={classes.screen}>
        <PageHeader title={title} />
        <Panel>
          <EmptyState title={t('pms.equipment.noVessel')} />
        </Panel>
      </div>
    );
  }
  const failed = choice.failed ?? (data.kind === 'error' ? data.code : null);

  return (
    <div className={classes.screen} data-pms-screen>
      <PageHeader
        title={title}
        description={t('pms.work.plan.description')}
        actions={
          <span className={work.headerActions}>
            <VesselPicker
              scope={source.scope}
              vessels={choice.vessels}
              value={vesselId}
              onChange={(id) => setQuery({ vessel: id, job: null, decide: null, newJob: null })}
            />
            {cmImportAllowed(source, ready?.points ?? null) ? (
              <Button
                variant="default"
                leftSection={<IconFileImport size={16} />}
                onClick={() => setImporting(true)}
              >
                {t('pms.condition.cm.open')}
              </Button>
            ) : null}
            {tab === 'jobs' && actions.create && source.jobEditor ? (
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setQuery({ newJob: '1' })}
              >
                {t('pms.work.job.new')}
              </Button>
            ) : null}
          </span>
        }
      />
      {notice ? (
        <p className={work.error} role="alert">
          {notice}
        </p>
      ) : null}
      <SetupChangesBanner
        setup={ready?.jobs?.setup}
        listPath={source.setupChanges && vesselId ? setupChangesPath(vesselId) : undefined}
      />
      <Tabs
        value={tab}
        onChange={(value) => setQuery({ tab: value === 'plan' ? null : value })}
        className={work.pageTabs}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="plan">{t('pms.work.plan.tabs.plan')}</Tabs.Tab>
          <Tabs.Tab value="jobs">{t('pms.work.plan.tabs.jobs')}</Tabs.Tab>
          {instructions ? (
            <Tabs.Tab value="instructions">{t('pms.work.plan.tabs.instructions')}</Tabs.Tab>
          ) : null}
        </Tabs.List>

        {tab === 'plan' || tab === 'jobs' ? (
          <PlanFlagFilters
            flags={flags}
            onChange={(key, on) => setQuery({ [key]: on ? '1' : null })}
          />
        ) : null}
        <Tabs.Panel value="plan">
          {failed ? (
            <Panel>
              <LoadFailed
                code={failed}
                onRetry={() => {
                  choice.retry();
                  reload();
                }}
              />
            </Panel>
          ) : (
            <div className={work.planGrid} aria-busy={!ready}>
              <div className={work.planMain}>
                <MaintenancePlanPanel
                  today={today}
                  plans={shownPlans}
                  pageSize={15}
                  allPeriods={planFlagsSpanPeriods(flags)}
                  onOpen={(plan) => setQuery({ job: plan.jobId })}
                  onDecide={(plan, kind) => {
                    const item = pendingFor(plan, kind);
                    if (item) setQuery({ decide: item.recordId });
                  }}
                  {...(rowActions ? { rowActions } : {})}
                />
              </div>
              <div className={work.planSide}>
                <UpcomingTasksPanel today={today} plans={shownPlans} />
                <PmsCalendar today={today} marks={marks} />
              </div>
              {decidable.length > 0 ? (
                <div className={work.planWide}>
                  <PendingDecisions
                    items={decidable}
                    onOpen={(item) => setQuery({ decide: item.recordId })}
                  />
                </div>
              ) : null}
            </div>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="jobs">
          {failed ? (
            <Panel>
              <LoadFailed code={failed} onRetry={reload} />
            </Panel>
          ) : (
            <JobLibrary
              jobs={ready?.jobs.items ?? null}
              plans={plansByJob}
              tree={ready?.tree ?? null}
              editedOnBoard={actions.editedOnBoard}
              canCreate={actions.create && Boolean(source.jobEditor)}
              flags={flags}
              isClass={(job) => classItemOf(ready?.tree ?? null, job)}
              onOpen={(job) => setQuery({ job: job.id })}
              onNew={() => setQuery({ newJob: '1' })}
            />
          )}
        </Tabs.Panel>

        {instructions ? <Tabs.Panel value="instructions">{instructions}</Tabs.Panel> : null}
      </Tabs>

      {ready ? (
        <JobCardDrawer
          opened={openJob !== null || newJob}
          job={openJob}
          plan={openPlan}
          equipmentId={null}
          nodes={ready.tree.nodes}
          today={today}
          actions={source.jobEditor ? actions : { ...actions, create: false, edit: false }}
          instructions={ready.instructions}
          onClose={() => setQuery({ job: null, newJob: null })}
          onSave={saveJob}
          {...(source.work ? { onRequestInterval: (job: JobView) => setIntervalJob(job) } : {})}
          {...(source.office?.sendIntervalChange
            ? { onOfficeInterval: (job: JobView) => setOfficeIntervalJob(job) }
            : {})}
          {...(openJob && intervalHistory?.jobId === openJob.id
            ? { intervalHistory: intervalHistory.entries, changeLog: intervalHistory.changes }
            : {})}
          {...(source.measurementPoints
            ? {
                measurement: {
                  points: ready.points.items,
                  can: source.pointEditor
                    ? ready.points.can
                    : { create: false, edit: false, delete: false },
                  onCreate: async (input) => {
                    await source.pointEditor?.create(ready.vesselId, input);
                    reload();
                  },
                  onUpdate: async (point, patch) => {
                    await source.pointEditor?.update(point.id, point.rowVersion, patch);
                    reload();
                  },
                  onRemove: async (point, reason) => {
                    await source.pointEditor?.remove(point.id, point.rowVersion, reason);
                    reload();
                  },
                },
              }
            : {})}
        />
      ) : null}

      {source.cm && ready ? (
        <CmImportDialog
          opened={importing}
          scope={source.scope}
          vesselId={ready.vesselId}
          importer={source.cm}
          pointNames={new Map(ready.points.items.map((p) => [p.id, p.name]))}
          onClose={() => setImporting(false)}
          onApplied={reload}
        />
      ) : null}

      {source.work ? (
        <>
          <IntervalChangeDialog
            opened={intervalJob !== null}
            job={intervalJob}
            onClose={() => setIntervalJob(null)}
            onSubmit={async (job, input) => {
              await source.work?.requestIntervalChange(job.id, input);
              setIntervalJob(null);
              reload();
            }}
          />
          <PostponeDialog
            opened={postponePlan !== null}
            plan={postponePlan}
            classItem={classItemOf(ready?.tree ?? null, jobsById.get(postponePlan?.jobId ?? ''))}
            onClose={() => {
              setPostponing(null);
              if (postponeParam) setQuery({ postpone: null });
            }}
            onSubmit={async (input) => {
              await source.work?.postpone(input);
              setPostponing(null);
              if (postponeParam) setQuery({ postpone: null });
              reload();
            }}
          />
        </>
      ) : null}

      {source.office?.sendIntervalChange ? (
        <IntervalChangeDialog
          opened={officeIntervalJob !== null}
          variant="office"
          job={officeIntervalJob ? withEffectiveInterval(officeIntervalJob, openPlan) : null}
          onClose={() => setOfficeIntervalJob(null)}
          onSubmit={async (job, input) => {
            await source.office?.sendIntervalChange?.(job.id, input);
            setOfficeIntervalJob(null);
            reload();
          }}
        />
      ) : null}

      {source.scope === 'ship' ? (
        <PostponementDecisionDrawer
          opened={decision !== null}
          item={decision}
          onClose={() => setQuery({ decide: null })}
          onDecide={async (item, input) => {
            await source.work?.decidePostponement(item.recordId, input);
            setQuery({ decide: null });
            reload();
          }}
        />
      ) : (
        <OfficeDecisionDrawer
          opened={decision !== null}
          item={decision}
          onClose={() => setQuery({ decide: null })}
          onDecide={async (item, input) => {
            if (decisionKind(item) === 'interval') {
              await source.office?.decideIntervalChange(item.recordId, input);
            } else {
              await source.office?.decidePostponement(item.recordId, input);
            }
            setQuery({ decide: null });
            reload();
          }}
        />
      )}
    </div>
  );
}

/** Satır menüsünde iş emri: açık iş emri varsa ona götürür, yoksa yenisini açar. */
function WorkOrderMenuItem({
  open,
  onOpen,
  onCreate,
}: {
  open: WorkOrderView | null;
  onOpen: (wo: WorkOrderView) => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation();
  return open ? (
    <Menu.Item leftSection={<IconClipboardText size={15} />} onClick={() => onOpen(open)}>
      {t('pms.work.plan.openWorkOrder', { no: open.woNo })}
    </Menu.Item>
  ) : (
    <Menu.Item leftSection={<IconClipboardPlus size={15} />} onClick={onCreate}>
      {t('pms.equipment.quickLinks.createWorkOrder')}
    </Menu.Item>
  );
}

/**
 * Ofis penceresi güncel aralığı plandaki etkin aralıkla gösterir (ADR 0104): iş tanımındaki aralık
 * gemideki değişikliklerden önceki değerdir. Ölçüm noktaları gemide kalır.
 */
export function withEffectiveInterval(job: JobView, plan: PlanItem | null): JobView {
  if (!plan || plan.jobId !== job.id) return job;
  return {
    ...job,
    interval: {
      calendar: plan.effectiveInterval.calendar ?? null,
      runningHours: plan.effectiveInterval.runningHours ?? null,
      conditionPointIds: job.interval.conditionPointIds,
    },
  };
}

/** Kişinin karar vereceği kayıtlar ("Pending Decisions"). */
export function PendingDecisions({
  items,
  onOpen,
}: {
  items: readonly PendingItem[];
  onOpen: (item: PendingItem) => void;
}) {
  const { t } = useTranslation();
  return (
    <Panel title={t('pms.work.decision.panel')}>
      <ul className={work.pendingList}>
        {items.map((item) => (
          <li key={item.recordId}>
            <span className={work.pendingText}>
              <PmsBadge status="pending">
                {t(`pms.work.decision.kind.${decisionKind(item)}`)}
              </PmsBadge>
              <span>
                {[item.jobCode, item.jobTitle].filter(Boolean).join(' · ')}
                {item.equipmentName ? ` — ${item.equipmentName}` : ''}
              </span>
            </span>
            <Button size="xs" variant="light" onClick={() => onOpen(item)}>
              {t('pms.work.decision.review')}
            </Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

type JobSortKey =
  | 'code'
  | 'title'
  | 'component'
  | 'workType'
  | 'frequency'
  | 'lastDone'
  | 'nextDue'
  | 'status';

/**
 * Job Library (görev kütüphanesi; PMS belgesi §7): kod, iş, bileşen, iş türü, sıklık, son ve
 * sıradaki yapılış, durum. Aranır, sıralanır ve sayfalanır; satır Job Card'ı açar.
 */
export function JobLibrary({
  jobs,
  plans,
  tree,
  editedOnBoard,
  canCreate,
  flags = {},
  isClass,
  onOpen,
  onNew,
}: {
  jobs: readonly JobView[] | null;
  plans: ReadonlyMap<string, PlanItem>;
  tree: EquipmentTree | null;
  editedOnBoard: boolean;
  canCreate: boolean;
  /** Klas, kritik, gecikmiş, ertelenmiş [NR496 4.4.2b, 4.5.3e]; planı olmayan iş süzgeçte görünmez. */
  flags?: PlanFlags;
  isClass?: (job: JobView) => boolean;
  onOpen: (job: JobView) => void;
  onNew: () => void;
}) {
  const { t, i18n } = useTranslation();
  const tableBox = useFitColumns<HTMLElement>();
  // Dar tabloda gizlenme sırası (`fit-columns.ts`): son yapılış, iş türü, sıklık
  const drop: Record<string, number | undefined> = { lastDone: 1, workType: 2, frequency: 3 };
  const k = (key: string) => t(`pms.work.library.${key}`);
  const language = i18n.resolvedLanguage ?? 'en';
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort<JobSortKey>>({ key: 'code', dir: 'asc' });
  const [page, setPage] = useState(1);
  const names = useMemo(() => new Map((tree?.nodes ?? []).map((n) => [n.id, n.name])), [tree]);
  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('en');
    const flagged = Object.values(flags).some(Boolean);
    const classOf = isClass ?? ((job: JobView) => classItemOf(tree, job));
    const list = (jobs ?? []).filter((job) => {
      if (flagged) {
        const plan = plans.get(job.id);
        if (
          !plan ||
          filterPlanFlags(
            [plan],
            flags,
            () => classOf(job),
            () => job.alarmTest === true,
          ).length === 0
        ) {
          return false;
        }
      }
      return (
        needle === '' ||
        [job.code, job.title, names.get(job.equipmentId) ?? ''].some((v) =>
          v.toLocaleLowerCase('en').includes(needle),
        )
      );
    });
    const value = (job: JobView): string => {
      const plan = plans.get(job.id);
      switch (sort.key) {
        case 'code':
          return job.code;
        case 'title':
          return job.title;
        case 'component':
          return names.get(job.equipmentId) ?? '';
        case 'workType':
          return job.workType;
        case 'frequency':
          return frequencyLabel(job.interval, t, language);
        case 'lastDone':
          return plan?.lastDone.doneOn ?? '';
        case 'nextDue':
          return plan?.due.dueOn ?? '9999';
        case 'status':
          return plan?.status ?? '';
      }
    };
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort(
      (a, b) => sign * value(a).localeCompare(value(b)) || a.code.localeCompare(b.code),
    );
  }, [jobs, plans, names, search, sort, t, language, flags, isClass, tree]);
  const pageView = paginate(rows, page, 12);
  const columns: [JobSortKey, string][] = [
    ['code', k('columns.code')],
    ['title', k('columns.title')],
    ['component', k('columns.component')],
    ['workType', k('columns.workType')],
    ['frequency', k('columns.frequency')],
    ['lastDone', k('columns.lastDone')],
    ['nextDue', k('columns.nextDue')],
    ['status', k('columns.status')],
  ];
  return (
    <Panel title={k('title')}>
      <div className={work.toolbar}>
        <TextInput
          className={work.search}
          size="sm"
          placeholder={k('search')}
          aria-label={k('search')}
          leftSection={<IconSearch size={14} aria-hidden />}
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            setPage(1);
          }}
        />
        {editedOnBoard ? (
          <span className={work.note}>{t('pms.equipment.details.editedOnBoard')}</span>
        ) : null}
      </div>
      {jobs === null ? (
        <RowsSkeleton rows={8} />
      ) : jobs.length === 0 ? (
        <EmptyState
          title={k('empty')}
          description={canCreate ? k('emptyHint') : undefined}
          action={
            canCreate ? (
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={onNew}>
                {t('pms.work.job.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <section
            ref={tableBox}
            className={classes.tableBox}
            aria-label={t('pms.equipment.tableOf', { name: k('title') })}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table} data-cards>
              <thead>
                <tr>
                  {columns.map(([key, name]) => (
                    <SortableTh
                      key={key}
                      column={key}
                      label={name}
                      sort={sort}
                      drop={drop[key]}
                      onSort={(next) => {
                        setSort(next);
                        setPage(1);
                      }}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageView.rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className={classes.emptyCell}>
                      <EmptyState title={k('noMatch')} />
                    </td>
                  </tr>
                ) : (
                  pageView.rows.map((job) => {
                    const plan = plans.get(job.id);
                    return (
                      <tr key={job.id}>
                        <td data-label={k('columns.code')}>
                          <UnstyledButton className={work.linkButton} onClick={() => onOpen(job)}>
                            {job.code}
                          </UnstyledButton>
                          {job.alarmTest ? (
                            <span className={work.note}> · {k('alarmTestBadge')}</span>
                          ) : null}
                        </td>
                        <td data-label={k('columns.title')} className={work.wrapCell}>
                          {job.title}
                        </td>
                        <td data-label={k('columns.component')}>
                          {names.get(job.equipmentId) ?? '—'}
                        </td>
                        <td data-label={k('columns.workType')} data-drop={drop.workType}>
                          {t(`pms.work.workTypes.${job.workType}`, { defaultValue: job.workType })}
                        </td>
                        <td data-label={k('columns.frequency')} data-drop={drop.frequency}>
                          {frequencyLabel(job.interval, t, language)}
                        </td>
                        <td data-label={k('columns.lastDone')} data-drop={drop.lastDone}>
                          {formatDay(plan?.lastDone.doneOn)}
                        </td>
                        <td data-label={k('columns.nextDue')}>{formatDay(plan?.due.dueOn)}</td>
                        <td data-label={k('columns.status')}>
                          {plan ? (
                            <PmsBadge status={plan.status} />
                          ) : (
                            <PmsBadge status="cancelled">{k('inactive')}</PmsBadge>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </section>
          {rows.length > 12 ? (
            <TablePager
              page={pageView.page}
              pages={pageView.pages}
              summary={t('pms.work.showing', {
                from: pageView.from,
                to: pageView.to,
                total: pageView.total,
              })}
              label={t('pms.work.pages', { name: k('title') })}
              onPage={setPage}
            />
          ) : null}
        </>
      )}
    </Panel>
  );
}

/**
 * "Show only" süzgeç düğmeleri (BV uyum kontrolü §6 eksik 4): kritik, klas kalemi, gecikmiş ve
 * ertelenmiş işler [NR496 4.4.2b, 4.5.3e]. Seçimler birlikte daralır; adreste kalır.
 */
export function PlanFlagFilters({
  flags,
  onChange,
}: {
  flags: PlanFlags;
  onChange: (key: (typeof PLAN_FLAG_KEYS)[number], on: boolean) => void;
}) {
  const { t } = useTranslation();
  const label = t('pms.work.plan.flags.label');
  return (
    <fieldset className={work.flagFilters} aria-label={label}>
      <legend className={work.flagLegend}>{label}</legend>
      {PLAN_FLAG_KEYS.map((key) => (
        <Chip
          key={key}
          size="xs"
          variant="outline"
          checked={flags[key] === true}
          onChange={(on) => onChange(key, on)}
          {...({ 'data-plan-flag': key } as Record<string, string>)}
        >
          {t(`pms.work.plan.flags.${key}`)}
        </Chip>
      ))}
    </fieldset>
  );
}
